function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}

function haversineDistance(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

function initStoreLocator(root) {
  if (root.dataset.storeLocatorInitialized === 'true') return;
  root.dataset.storeLocatorInitialized = 'true';

  const listEl = root.querySelector('[data-store-locator-list]');
  const detailEl = root.querySelector('[data-store-locator-detail]');
  const grid = root.querySelector('[data-store-locator-grid]');
  const cards = Array.from(root.querySelectorAll('[data-store-locator-card]'));
  const stateSelect = root.querySelector('[data-store-locator-state]');
  const citySelect = root.querySelector('[data-store-locator-city]');
  const resetButton = root.querySelector('[data-store-locator-reset]');
  const countEl = root.querySelector('[data-store-locator-count]');
  const messageEl = root.querySelector('[data-store-locator-message]');
  const retryButton = root.querySelector('[data-store-locator-retry]');
  const emptyEl = root.querySelector('[data-store-locator-empty]');
  const nearbyRadius = parseFloat(root.dataset.nearbyRadius) || 25;

  let userCoords = null;

  const storesBySlug = new Map();
  cards.forEach((card) => {
    if (card.dataset.slug) storesBySlug.set(card.dataset.slug, card);
  });

  function buildCityOptionsByState() {
    const map = new Map();
    cards.forEach((card) => {
      const state = card.dataset.state || '';
      const city = card.dataset.city || '';
      if (!state || !city) return;
      if (!map.has(state)) map.set(state, new Set());
      map.get(state).add(city);
    });
    return map;
  }

  const cityOptionsByState = buildCityOptionsByState();

  function rebuildCityOptions() {
    if (!citySelect) return;

    const selectedState = stateSelect ? stateSelect.value : 'all';
    const previousCity = citySelect.value;
    const allLabel = citySelect.dataset.allLabel || 'All';

    const cities = new Set();
    if (selectedState === 'all') {
      cards.forEach((card) => {
        if (card.dataset.city) cities.add(card.dataset.city);
      });
    } else {
      const stateCities = cityOptionsByState.get(selectedState);
      if (stateCities) stateCities.forEach((city) => cities.add(city));
    }

    const sortedCities = Array.from(cities).sort((a, b) => a.localeCompare(b));

    citySelect.innerHTML = '';
    const allOption = document.createElement('option');
    allOption.value = 'all';
    allOption.textContent = allLabel;
    citySelect.appendChild(allOption);

    sortedCities.forEach((city) => {
      const option = document.createElement('option');
      option.value = city;
      option.textContent = city;
      citySelect.appendChild(option);
    });

    citySelect.value = sortedCities.includes(previousCity) ? previousCity : 'all';
  }

  function applyFilters() {
    const state = stateSelect ? stateSelect.value : 'all';
    const city = citySelect ? citySelect.value : 'all';
    let visibleCount = 0;

    cards.forEach((card) => {
      const matchesState = state === 'all' || card.dataset.state === state;
      const matchesCity = city === 'all' || card.dataset.city === city;
      const visible = matchesState && matchesCity;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (countEl) countEl.textContent = `(${visibleCount})`;
    if (emptyEl) emptyEl.hidden = visibleCount !== 0;
  }

  function setMessage(text) {
    if (messageEl && text) messageEl.textContent = text;
  }

  function applyDistances() {
    if (!userCoords || !grid) return;

    const withDistance = [];
    const withoutDistance = [];

    cards.forEach((card) => {
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      const distanceEl = card.querySelector('[data-store-locator-distance]');

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const distanceKm = haversineDistance(userCoords.latitude, userCoords.longitude, lat, lng);
        card.dataset.distance = distanceKm;
        if (distanceEl) {
          distanceEl.textContent = `${distanceKm < 10 ? distanceKm.toFixed(1) : Math.round(distanceKm)} km away`;
          distanceEl.dataset.hasDistance = 'true';
        }
        withDistance.push(card);
      } else {
        withoutDistance.push(card);
      }
    });

    withDistance.sort((a, b) => parseFloat(a.dataset.distance) - parseFloat(b.dataset.distance));
    withDistance.concat(withoutDistance).forEach((card) => grid.appendChild(card));
  }

  function requestLocation() {
    if (!('geolocation' in navigator)) {
      return;
    }

    setMessage(root.dataset.locatingMessage);
    if (retryButton) retryButton.setAttribute('aria-busy', 'true');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        userCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        applyDistances();
        setMessage(root.dataset.nearestMessage);
        if (retryButton) retryButton.removeAttribute('aria-busy');
      },
      () => {
        if (messageEl) messageEl.textContent = '';
        if (retryButton) retryButton.removeAttribute('aria-busy');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  function buildMapEmbedSrc(card) {
    const mapHref = card.dataset.mapHref || '';
    const isEmbeddable = /\/maps\/embed|output=embed/.test(mapHref);
    if (isEmbeddable) return mapHref;

    const lat = parseFloat(card.dataset.lat);
    const lng = parseFloat(card.dataset.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
    }
    if (card.dataset.address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(card.dataset.address)}&z=15&output=embed`;
    }
    return '';
  }

  function findNearbyStores(card) {
    const lat = parseFloat(card.dataset.lat);
    const lng = parseFloat(card.dataset.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];

    return cards
      .filter((other) => other !== card)
      .map((other) => {
        const otherLat = parseFloat(other.dataset.lat);
        const otherLng = parseFloat(other.dataset.lng);
        if (!Number.isFinite(otherLat) || !Number.isFinite(otherLng)) return null;
        return { card: other, distance: haversineDistance(lat, lng, otherLat, otherLng) };
      })
      .filter((entry) => entry && entry.distance <= nearbyRadius)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }

  function renderDetail(card) {
    const mapSrc = buildMapEmbedSrc(card);
    const nearby = findNearbyStores(card);

    const rows = [
      [root.dataset.addressLabel, card.dataset.address],
      [root.dataset.hoursLabel, card.dataset.hours],
      [root.dataset.phoneLabel, card.dataset.phone || root.dataset.phoneUnavailable],
      [root.dataset.emailLabel, card.dataset.email],
    ];

    const rowsHtml = rows
      .filter(([, value]) => value)
      .map(([label, value]) => `<p class="store-locator__detail-row"><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</p>`)
      .join('');

    const actionsHtml = `<div class="store-locator__detail-actions">${
      card.dataset.mapHref
        ? `<a href="${escapeHtml(card.dataset.mapHref)}" target="_blank" rel="noopener" class="button button--secondary">${escapeHtml(root.dataset.getDirectionsLabel)}</a>`
        : ''
    }${
      card.dataset.phone
        ? `<a href="tel:${escapeHtml(card.dataset.phone)}" class="button">${escapeHtml(root.dataset.callNowLabel)}</a>`
        : ''
    }</div>`;

    const nearbyListHtml = nearby.length
      ? `<ul class="store-locator__nearby-list">${nearby
          .map(
            (entry) => `<li>
              <button type="button" class="store-locator__nearby-item" data-store-locator-goto="${escapeHtml(entry.card.dataset.slug)}">
                <span>${escapeHtml(entry.card.dataset.name)}</span>
                <span>${entry.distance < 10 ? entry.distance.toFixed(1) : Math.round(entry.distance)} km</span>
              </button>
            </li>`
          )
          .join('')}</ul>`
      : `<p class="store-locator__nearby-empty">${(root.dataset.noNearbyTemplate || '').replace('{{ radius }}', nearbyRadius)}</p>`;

    detailEl.innerHTML = `
      <button type="button" class="store-locator__back" data-store-locator-back>${escapeHtml(root.dataset.backLabel)}</button>
      <div class="store-locator__detail-layout">
        <div class="store-locator__detail-info">
          ${
            card.dataset.image
              ? `<div class="store-locator__detail-media"><img src="${escapeHtml(card.dataset.image)}" alt="${escapeHtml(card.dataset.name)}" loading="lazy"></div>`
              : ''
          }
          <h2 class="store-locator__detail-name">${escapeHtml(card.dataset.name)}</h2>
          <p class="store-locator__detail-location">${escapeHtml(card.dataset.city)}, ${escapeHtml(card.dataset.state)}</p>
          ${rowsHtml}
          ${actionsHtml}
          <div class="store-locator__nearby">
            <h3 class="store-locator__nearby-heading">${escapeHtml(root.dataset.nearbyLabel)}</h3>
            ${nearbyListHtml}
          </div>
        </div>
        <div class="store-locator__detail-map">
          ${
            mapSrc
              ? `<iframe src="${escapeHtml(mapSrc)}" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`
              : ''
          }
        </div>
      </div>
    `;

    detailEl.querySelector('[data-store-locator-back]')?.addEventListener('click', goToList);
    detailEl.querySelectorAll('[data-store-locator-goto]').forEach((button) => {
      button.addEventListener('click', () => goToStore(button.dataset.storeLocatorGoto));
    });
  }

  function showList() {
    if (detailEl) {
      detailEl.hidden = true;
      detailEl.innerHTML = '';
    }
    if (listEl) listEl.hidden = false;
  }

  function showDetail(slug) {
    const card = storesBySlug.get(slug);
    if (!card || !detailEl) {
      showList();
      return;
    }
    renderDetail(card);
    if (listEl) listEl.hidden = true;
    detailEl.hidden = false;
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goToStore(slug) {
    if (!storesBySlug.has(slug)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('store', slug);
    history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
    showDetail(slug);
  }

  function goToList() {
    const url = new URL(window.location.href);
    url.searchParams.delete('store');
    history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
    showList();
  }

  function syncFromUrl() {
    const slug = new URLSearchParams(window.location.search).get('store');
    if (slug && storesBySlug.has(slug)) {
      showDetail(slug);
    } else {
      showList();
    }
  }

  stateSelect?.addEventListener('change', () => {
    rebuildCityOptions();
    applyFilters();
  });

  citySelect?.addEventListener('change', applyFilters);

  resetButton?.addEventListener('click', () => {
    if (stateSelect) stateSelect.value = 'all';
    rebuildCityOptions();
    if (citySelect) citySelect.value = 'all';
    applyFilters();
  });

  retryButton?.addEventListener('click', requestLocation);

  root.querySelectorAll('[data-store-locator-state-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const state = event.currentTarget.dataset.storeLocatorStateLink;
      if (stateSelect && state) {
        stateSelect.value = state;
        rebuildCityOptions();
        applyFilters();
      }
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  cards.forEach((card) => {
    card.addEventListener('click', (event) => {
      if (event.target.closest('a, button')) return;
      if (card.dataset.slug) goToStore(card.dataset.slug);
    });
  });

  window.addEventListener('popstate', syncFromUrl);

  rebuildCityOptions();
  applyFilters();
  syncFromUrl();

  if (root.dataset.geolocationEnabled === 'true') {
    requestLocation();
  }
}

function initStoreLocatorStates(root) {
  if (root.dataset.statesInitialized === 'true') return;
  root.dataset.statesInitialized = 'true';

  const toggle = root.querySelector('[data-store-locator-states-toggle]');
  if (!toggle) return;

  toggle.addEventListener('click', () => {
    const expanded = root.getAttribute('data-expanded') === 'true';
    root.setAttribute('data-expanded', expanded ? 'false' : 'true');
    toggle.textContent = expanded ? toggle.dataset.moreLabel : toggle.dataset.lessLabel;
  });
}

function initStoreLocatorsWithin(scope) {
  const locators = scope.matches?.('.store-locator') ? [scope] : Array.from(scope.querySelectorAll('.store-locator'));
  locators.forEach(initStoreLocator);

  const statesLists = scope.matches?.('.store-locator__states')
    ? [scope]
    : Array.from(scope.querySelectorAll('.store-locator__states'));
  statesLists.forEach(initStoreLocatorStates);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initStoreLocatorsWithin(document));
} else {
  initStoreLocatorsWithin(document);
}

document.addEventListener('shopify:section:load', (event) => initStoreLocatorsWithin(event.target));
